import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

// IDs updated in 2026-04-14/15 image work sessions
const UPDATED_IDS = [
  'artejuv-100',
  'biltree-600',
  'ivecop',
  'iverheal',
  'ivernom-oral',
  'juvpen-444',
  'minoz',
  'niclobest-500',
  'niclosig',
  'irovel-h',
  'teraless-hytrin',
  'cilniheal',
  'lipidator',
  'aldactone-rpg-25',
  'finobrate-lipidil',
  'cynomycin',
  'prazinec-600',
  'mebenza-100',
  'cresar-micardis',
  'raloxiheal-60',
];

interface ReviewRow {
  id: string;
  name: string;
  manufacturer: string;
  image: string;
  variants: Array<{ id: string; strength_label: string; image: string }>;
}

const AdminImageReview: React.FC = () => {
  const [rows, setRows] = useState<ReviewRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data: products } = await supabase
        .from('products')
        .select('id, name, manufacturer, image')
        .in('id', UPDATED_IDS);
      const { data: variants } = await supabase
        .from('product_variants')
        .select('id, product_id, strength_label, image, display_order')
        .in('product_id', UPDATED_IDS)
        .order('display_order');
      const byProduct = new Map<string, ReviewRow['variants']>();
      (variants || []).forEach((v: any) => {
        const arr = byProduct.get(v.product_id) || [];
        arr.push({ id: v.id, strength_label: v.strength_label, image: v.image });
        byProduct.set(v.product_id, arr);
      });
      const ordered = UPDATED_IDS
        .map(id => (products || []).find((p: any) => p.id === id))
        .filter(Boolean)
        .map((p: any) => ({ ...p, variants: byProduct.get(p.id) || [] } as ReviewRow));
      setRows(ordered);
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="p-8">Loading…</div>;

  return (
    <div className="p-6 md:p-10 max-w-[1400px] mx-auto">
      <div className="mb-6">
        <h1 className="font-display text-3xl text-neutral-950 mb-2">Image Review — Recent Updates</h1>
        <p className="text-neutral-500 text-sm">
          {rows.length} products updated. Click a card to open the product page.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {rows.map(row => (
          <div key={row.id} className="border border-neutral-200 rounded-lg overflow-hidden bg-white">
            <Link to={`/product/${row.id}`} target="_blank" className="block">
              <div className="aspect-square bg-neutral-50 flex items-center justify-center overflow-hidden">
                <img
                  src={row.image}
                  alt={row.name}
                  className="w-full h-full object-contain"
                  loading="lazy"
                  onError={e => { (e.target as HTMLImageElement).style.opacity = '0.25'; }}
                />
              </div>
              <div className="p-3">
                <div className="text-xs font-mono text-neutral-400 mb-1">{row.id}</div>
                <div className="text-sm font-medium text-neutral-900 line-clamp-2">{row.name}</div>
                <div className="text-xs text-neutral-500 mt-1">{row.manufacturer}</div>
              </div>
            </Link>

            {row.variants.length > 1 && (
              <div className="border-t border-neutral-200 bg-neutral-50 p-2">
                <div className="text-[10px] uppercase tracking-wide text-neutral-400 mb-1">
                  Variants ({row.variants.length})
                </div>
                <div className="grid grid-cols-3 gap-1">
                  {row.variants.map(v => (
                    <div key={v.id} className="aspect-square bg-white rounded overflow-hidden border border-neutral-200">
                      <img
                        src={v.image}
                        alt={v.strength_label}
                        className="w-full h-full object-contain"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminImageReview;
